/* eslint-disable react/display-name */
import React, { memo, useCallback, useEffect } from 'react';
import { withScreenSize } from '../common/utilities/withScreenSize.jsx';
import { reduxConnect } from '../../common';
import { dataLayer } from '@weatherbug/common';
import { translate } from  '../../common/translation/translate.js';
import { 
    IStoreState, 
    OutdoorSportsDetailDataType, 
    OutdoorSportsForecastRecordType, 
    OutdoorSportsIndexLegendLevel, 
    OutdoorSportsKeyIndicatorsType,
    Story 
} from '../common/interfaces/types';
import { LifePageContentTitle } from '../common/life/lifePageContentTitle';
import { LifeHeaderText } from '../common/life/lifeIntroText.js';
import styled from 'styled-components';
import { largeMediaQuery, mediumMediaQuery, smallMediaQuery } from '../../common/utilities/sizeHelpers.js';
import { DisplayAdWidget } from '../ads/displayAdWidget.jsx';
import { OutdoorSportsSafetyTips } from './outdoorSportsSafetyTips';
import { OutdoorSportsKeyIndicators } from './outdoorSportsKeyIndicators';
import { HourlyChart } from './hourlyChart';
import { HourlyOutdoorIndex } from './hourlyOutdoorIndex';
import { OutdoorStories } from './outdoorStories';
import { isDataError } from '../common/utilities/isDataError';
import { getNewsStories } from '../../common/actions/stories/storiesAction';
import { DisneyWeatherPartnershipSection } from './disneyWeatherPartnershipSection';
import { DisplayAdUnit } from '../../containers/ads/displayAdUnitContainer';

const { 
    getOutdoorSportDetailDataByLatLon, 
    getOutdoorSportLegendDataByLatLon
} = dataLayer.actions;
const storiesType = 'Outdoor-sports';

interface OutdoorSportsPageType {
    latitude: string;
    longitude: string;
    authId: string;
    secret: string;
    stdParameters: Record<string, unknown>;
    contentMgmtBaseUrl: string;
    forecastIconResourceList: string;
    forecastIconSet: string;
    forecastIconSize: string;
    outdoorSportsBaseUrl: string;
    outdoorSportsStoriesBaseUrl: string;
    getOutdoorSportDetailDataByLatLon: typeof getOutdoorSportDetailDataByLatLon;
    getOutdoorSportLegendDataByLatLon: typeof getOutdoorSportLegendDataByLatLon;
    getNewsStories: typeof getNewsStories;
    localityLabel: string;
    outdoorSportsDataIsFetching: boolean;
    outdoorSportsDetailData: OutdoorSportsDetailDataType;
    keyIndicators: OutdoorSportsKeyIndicatorsType;
    forecastRecords: OutdoorSportsForecastRecordType[];
    outdoorSportsIndexLegendLevels: OutdoorSportsIndexLegendLevel[];
    isApiError: boolean;
    sportsStories: Story[];
    tempUnit: number;
    speedUnit: number;
    distanceUnit: number;
    isDesktop: boolean;
    isTablet: boolean;
    isMobile: boolean;
    error: Record<string, unknown>;
    enableSourceParameterOnStoriesLinks: boolean;
}

const mapStoreStateToProps = (storeState: IStoreState) => {
    const { outdoorSports, units, newsStories } = storeState;
    const appState = storeState.appState.appState || {};
    const { authId, contentMgmtBaseUrl, enableSourceParameterOnStoriesLinks, forecastIconResourceList, forecastIconSet, forecastIconSize, outdoorSportsBaseUrl, secret, stdParameters, user } = appState;
    const { error, outdoorSportsDataIsFetching, outdoorSportsDetailData, outdoorSportsLegendData } = outdoorSports;
    const localityLabel = user?.ActiveLocation?.Locality?.Label;

    return {
        latitude: user.ActiveLocation.Locality.Latitude,
        longitude: user.ActiveLocation.Locality.Longitude,
        authId,
        contentMgmtBaseUrl,
        enableSourceParameterOnStoriesLinks,
        forecastIconResourceList,
        forecastIconSet,
        forecastIconSize,
        secret,
        stdParameters,
        outdoorSportsBaseUrl,
        localityLabel,
        outdoorSportsDataIsFetching,
        outdoorSportsDetailData,
        keyIndicators: outdoorSports?.outdoorSportsDetailData?.keyIndicators,
        forecastRecords: outdoorSports?.outdoorSportsDetailData?.forecastRecords,
        outdoorSportsIndexLegendLevels: outdoorSportsLegendData?.outdoorSportsIndexLegendLevels,
        sportsStories: newsStories?.stories,
        tempUnit: units.tempUnit,
        speedUnit: units.windUnit,
        distanceUnit: units.unitSystem,
        error
    };
};

const mapDispatchToProps = {
    getOutdoorSportDetailDataByLatLon,
    getOutdoorSportLegendDataByLatLon,
    getNewsStories
};

const OutdoorSportsPage = withScreenSize(reduxConnect(mapStoreStateToProps, mapDispatchToProps)(memo(({
    latitude,
    longitude,
    authId,
    secret,
    stdParameters,
    contentMgmtBaseUrl,
    enableSourceParameterOnStoriesLinks,
    forecastIconResourceList,
    forecastIconSet,
    forecastIconSize,
    outdoorSportsBaseUrl,
    getOutdoorSportDetailDataByLatLon,
    getOutdoorSportLegendDataByLatLon,
    getNewsStories,
    localityLabel = '',
    outdoorSportsDataIsFetching = false,
    keyIndicators,
    forecastRecords,
    outdoorSportsIndexLegendLevels,
    sportsStories,
    tempUnit,
    speedUnit,
    distanceUnit,
    isDesktop,
    isTablet,
    isMobile,
    error
}: OutdoorSportsPageType) => {
    const introText = `${translate('outdoorSportsGuideFor')} ${localityLabel}`;
    const isApiError = isDataError(error);

    const fetchOutdoorSportsDetailData = useCallback(() => {
        try {
            if (latitude && longitude && outdoorSportsBaseUrl && authId && secret && tempUnit && speedUnit && distanceUnit) {
                getOutdoorSportDetailDataByLatLon(
                    latitude,
                    longitude,
                    outdoorSportsBaseUrl, 
                    authId,
                    secret, 
                    stdParameters, 
                    tempUnit,
                    speedUnit,
                    distanceUnit,
                    true, 
                    false
                );
            }
        } catch (error) {
            console.log(error);
        }
    }, [latitude, longitude, outdoorSportsBaseUrl, authId, secret, tempUnit, speedUnit, distanceUnit]);

    const fetchOutdoorSportLegendData = useCallback(() => {
        try {
            if (latitude && longitude && outdoorSportsBaseUrl && authId && secret) {
                getOutdoorSportLegendDataByLatLon(
                    latitude,
                    longitude,
                    outdoorSportsBaseUrl, 
                    authId,
                    secret, 
                    stdParameters, 
                    true, 
                    false
                );
            }
        } catch (error) {
            console.log(error);
        }
    }, [latitude, longitude, outdoorSportsBaseUrl, authId, secret]);

    useEffect(() => {
        window.scrollTo(0, 0);
        getNewsStories({ baseUrl: location.origin, category: storiesType, count: 6 });
    }, []);

    useEffect(() => {
        fetchOutdoorSportsDetailData();
    }, [fetchOutdoorSportsDetailData]);
    
    useEffect(() => {
        fetchOutdoorSportLegendData();
    }, [fetchOutdoorSportLegendData]);

    return <>
        {isMobile 
                && <MobileBannerAd>
                    <DisplayAdUnit
                        width={320}
                        height={50}
                        slotId={'outdoorsports_mobile_banner_x83'}
                    />
                </MobileBannerAd>
        }
        <LifePageContentTitle pageTitle={translate('outdoorSports')} />
        <LifeHeaderText>{introText}</LifeHeaderText>
        <PageContainer>
            <TopContentContainer>
                <TopRowContentContainer>
                    <OutdoorSportsSafetyTips/>
                    <OutdoorSportsKeyIndicators 
                        outdoorSportsDataIsFetching={outdoorSportsDataIsFetching}
                        isApiError={isApiError}
                        sunriseDisplayStr={keyIndicators?.sunriseDisplayStr}
                        sunsetDisplayStr={keyIndicators?.sunsetDisplayStr}
                        feelsLikeTemperature={keyIndicators?.feelsLikeTemperature}
                        precipProbabilityPercent100={keyIndicators?.precipProbabilityPercent100}
                        uvDisplayStr={keyIndicators?.uvDisplayStr}
                        humidityPercent100={keyIndicators?.humidityPercent100}
                        windDisplayStr={keyIndicators?.windDisplayStr}
                        wetBulbTemperatureDisplayStr={keyIndicators?.wetBulbTemperatureDisplayStr}
                    />
                </TopRowContentContainer>
                {isTablet && <AdContainer>
                    <DisplayAdUnit
                        slotId={'outdoorsports_tablet_top_right_x16'}
                        height={600}
                        width={300}
                    />
                </AdContainer>}
                {isMobile && <AdContainer>
                    <DisplayAdUnit
                        slotId={'outdoorsports_mobile_indicators_x11'}
                        height={250}
                        width={300}
                    />
                </AdContainer>}
                {!isTablet && <LowerContentContainer>
                    <HourlyChart
                        outdoorSportsDataIsFetching={outdoorSportsDataIsFetching}
                        forecastRecords={forecastRecords}
                        isApiError={isApiError}
                        outdoorSportsIndexLegendLevels={outdoorSportsIndexLegendLevels}
                    />
                </LowerContentContainer>}
                {!isTablet && <LowerContentColumnContainer>
                    <HourlyOutdoorIndex
                        authId={authId}
                        secret={secret}
                        stdParameters={stdParameters}
                        contentMgmtBaseUrl={contentMgmtBaseUrl}
                        resourceList={forecastIconResourceList}
                        forecastIconSet={forecastIconSet}
                        forecastIconSize={forecastIconSize}
                        outdoorSportsDataIsFetching={outdoorSportsDataIsFetching}
                        forecastRecords={forecastRecords}
                        isApiError={isApiError}
                    />
                    <DisneyWeatherPartnershipSection />
                </LowerContentColumnContainer>}
                {!isTablet && <LowerContentContainer>
                    <OutdoorStories
                        outdoorSportsDataIsFetching={outdoorSportsDataIsFetching}
                        isApiError={isApiError}
                        sportsStories={sportsStories}
                        enableSourceParameterOnStoriesLinks={enableSourceParameterOnStoriesLinks}
                    />
                </LowerContentContainer>}
            </TopContentContainer>
            {isDesktop && <AdContainer>
                <DisplayAdUnit
                    slotId={'outdoorsports_desk_top_right'}
                    height={600}
                    width={300}
                />
                <OverrideAdContainerHeight>
                    <DisplayAdUnit
                        slotId={'outdoorsports_desk_bottom_right'}
                        height={600}
                        width={300}
                    />
                </OverrideAdContainerHeight>
            </AdContainer>}
            {isTablet && <>
                <LowerContentContainer>
                    <HourlyChart
                        outdoorSportsDataIsFetching={outdoorSportsDataIsFetching}
                        forecastRecords={forecastRecords}
                        isApiError={isApiError}
                        outdoorSportsIndexLegendLevels={outdoorSportsIndexLegendLevels}
                    />
                </LowerContentContainer>
                
            </>}
            {isTablet && <>
                <LowerContentColumnContainer>
                    <HourlyOutdoorIndex
                        authId={authId}
                        secret={secret}
                        stdParameters={stdParameters}
                        contentMgmtBaseUrl={contentMgmtBaseUrl}
                        resourceList={forecastIconResourceList}
                        forecastIconSet={forecastIconSet}
                        forecastIconSize={forecastIconSize}
                        outdoorSportsDataIsFetching={outdoorSportsDataIsFetching}
                        forecastRecords={forecastRecords}
                        isApiError={isApiError}
                    />
                    <DisneyWeatherPartnershipSection />
                </LowerContentColumnContainer>
            </>}
            {isTablet && <>
                <LowerContentContainer>
                    <OutdoorStories
                        outdoorSportsDataIsFetching={outdoorSportsDataIsFetching}
                        isApiError={isApiError}
                        sportsStories={sportsStories}
                        enableSourceParameterOnStoriesLinks={enableSourceParameterOnStoriesLinks}
                    />
                </LowerContentContainer>
            </>}
        </PageContainer>
    </>;
})));

const PageContainer = styled.div`
    display: flex;
    flex-direction: row;

    @media ${mediumMediaQuery} {
        flex-direction: column;
    }
`;

const MobileBannerAd = styled.div`
    margin-bottom: 20px;
`;

const TopContentContainer = styled.div`
    display: flex;
    flex-direction: column;
    width: 72%;

    @media ${mediumMediaQuery} {
        flex-direction: row;
        width: 100%;
    }

    @media ${smallMediaQuery} {
        width: 100%;
    }
`;

const TopRowContentContainer = styled.div`
    display: flex;
    flex-direction: row;
    width: 100%;
    margin-top: 10px;

    @media ${mediumMediaQuery} {
        flex-direction: column;
        width: 100%;
    }

    @media ${smallMediaQuery} {
        flex-direction: column;
    }
`;

const LowerContentContainer = styled.div`
    display: flex;
    flex-direction: row;
`;
const LowerContentColumnContainer = styled.div`
    display: flex;
    flex-direction: column;
    width: 100%;
    margin-bottom: 10px;

    @media ${largeMediaQuery} {
        padding-right: 10px;
    }
`;

const AdContainer = styled.div`
    display: flex;
    flex-direction: column;
    margin-top: 10px;

    @media ${mediumMediaQuery} {
        margin: 10px 0 0 10px;
    }
`;

const OverrideAdContainerHeight = styled.div`
    .content {
        height: 0;
    }
`;

export { OutdoorSportsPage as default };